import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { protectedResources } from '@/features/auth/authConfig';
import { ArtifactStatusNotification, SystemArtifactState } from '@/signalr/types/signalr.types';
import { useQueryClient } from '@tanstack/react-query';
import { useArtifactNotifications } from "@/signalr/hooks/useArtifactNotifications.tsx";
import { useRefreshNotifications } from "@/signalr/hooks/useRefreshNotifications.tsx";
import { useMsal } from '@azure/msal-react';
import apiClient from "@/shared/api/apiClient.ts";

// Constants
const MAX_CONNECTION_RETRIES = 3;
const RETRY_DELAY_BASE = 2000;
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 20000];
const ACTIVE_REFRESH_TIMEOUT = 180000; // 3 minutes (exceeds saga 2-minute timeout)

// Track pending connection attempts to prevent race conditions
let pendingConnectionSystemId: string | null = null;

type SignalRContextType = {
  connection: HubConnection | null;
  isConnected: boolean;
  error: Error | null;
  hasPersistedError: boolean;
  connectToSystem: (systemId: string) => Promise<void>;
  scheduleDisconnectFromSystem: (systemId: string) => void;
  disconnect: () => Promise<void>;
  currentSystemId: string | null;
  registerActiveRefresh: (systemId: string) => void;
  unregisterActiveRefresh: (systemId: string) => void;
  isRefreshActive: (systemId: string) => boolean;
  leaveSystemGroup: (systemId: string, connectionId: string) => Promise<void>;
};

const SignalRContext = createContext<SignalRContextType>({
  connection: null,
  isConnected: false,
  error: null,
  hasPersistedError: false,
  connectToSystem: async () => {
  },
  scheduleDisconnectFromSystem: () => {
  },
  disconnect: async () => {
  },
  currentSystemId: null,
  registerActiveRefresh: () => {
  },
  unregisterActiveRefresh: () => {
  },
  isRefreshActive: () => false,
  leaveSystemGroup: async () => {
  },
});

export const SignalRProvider: React.FC<{
  hubUrl: string;
  children: React.ReactNode;
}> = ({hubUrl, children}) => {
  const {instance: msalInstance} = useMsal();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasPersistedError, setHasPersistedError] = useState(false);
  const [currentSystemId, setCurrentSystemId] = useState<string | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);
  const systemConnectionTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const staleConnectionTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const errorPersistenceTimeout = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();
  const {showArtifactNotification} = useArtifactNotifications();

  // Track active refresh operations to prevent premature disconnection
  // Use ref for synchronous checks (no closure issues)
  const activeRefreshesRef = useRef<Set<string>>(new Set());
  const activeRefreshTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Helper to get active account (used multiple times)
  const getActiveAccount = useCallback(() => {
    return msalInstance?.getActiveAccount() || null;
  }, [msalInstance]);

  // Shared connection cleanup logic
  const cleanupConnection = useCallback(() => {
    const currentConnection = connectionRef.current;
    if (currentConnection) {
      currentConnection.stop().catch(err => console.error("Error stopping connection:", err));
      connectionRef.current = null;
    }
  }, []);

  // Effect to manage error persistence timer to only set hasPersistedError = true if error persists for 10 seconds
  useEffect(() => {
    if (error && currentSystemId) {
      // Start timer when error occurs
      errorPersistenceTimeout.current = setTimeout(() => {
        console.log('[SignalR] Error persisted for 10 seconds, showing warning');
        setHasPersistedError(true);
      }, (10 * 1000));
    } else {
      // Clear timer and persisted error flag when connection recovers or system cleared
      if (errorPersistenceTimeout.current) {
        clearTimeout(errorPersistenceTimeout.current);
        errorPersistenceTimeout.current = null;
      }
      setHasPersistedError(false);
    }

    return () => {
      if (errorPersistenceTimeout.current) {
        clearTimeout(errorPersistenceTimeout.current);
        errorPersistenceTimeout.current = null;
      }
    };
  }, [error, currentSystemId]);

  // Unregister an active refresh operation to allow disconnection
  const unregisterActiveRefresh = useCallback((systemId: string) => {
    activeRefreshesRef.current.delete(systemId);

    // Clear the timeout fallback
    const timeoutId = activeRefreshTimeouts.current.get(systemId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      activeRefreshTimeouts.current.delete(systemId);
    }

    console.log(`[Refresh] Unregistered active refresh for ${systemId}`);
  }, []);

  // Initialize refresh notifications hook after unregisterActiveRefresh is defined
  const {handleRefreshUpdate} = useRefreshNotifications(currentSystemId, unregisterActiveRefresh);

  // Join a system group using connection-based membership
  const joinSystemGroup = useCallback(async (systemId: string, connectionId: string) => {
    try {
      await apiClient.post('/join-system-group', null, {
        params: {systemId, connectionId}
      });
      console.log(`[SignalR] Joined system group ${systemId} with connection ${connectionId}`);
    } catch (error) {
      console.error(`[SignalR] Failed to join system group ${systemId}:`, error);
    }
  }, []);

  // Leave a system group (best-effort - connections auto-leave on disconnect anyway)
  const leaveSystemGroup = useCallback(async (systemId: string, connectionId: string) => {
    try {
      await apiClient.post('/leave-system-group', null, {
        params: {systemId, connectionId}
      });
      console.log(`[SignalR] Left system group ${systemId}`);
    } catch (error) {
      console.error(`[SignalR] Failed to leave system group ${systemId}:`, error);
      // Don't throw - leaving a group is best-effort
    }
  }, []);

  // Register an active refresh operation to prevent disconnection during saga execution
  const registerActiveRefresh = useCallback((systemId: string) => {
    activeRefreshesRef.current.add(systemId);
    console.log(`[Refresh] Registered active refresh for ${systemId}`);

    // Set timeout fallback in case notification never arrives (3 minutes > 2 minute saga timeout)
    const timeoutId = setTimeout(() => {
      console.warn(`[Refresh] Active refresh timeout for ${systemId} - auto-unregistering`);
      unregisterActiveRefresh(systemId);
    }, ACTIVE_REFRESH_TIMEOUT);

    activeRefreshTimeouts.current.set(systemId, timeoutId);
  }, [unregisterActiveRefresh]);

  const connectToSystem = useCallback(async (systemId: string) => {
    const account = getActiveAccount();
    if (!account) {
      console.warn('Cannot connect to system: No active account');
      setError(new Error('No active account'));
      return;
    }

    // Clear any pending disconnect timeouts for this system
    const existingTimeout = systemConnectionTimeouts.current.get(systemId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      systemConnectionTimeouts.current.delete(systemId);
    }

    const existingStaleTimeout = staleConnectionTimeouts.current.get(systemId);
    if (existingStaleTimeout) {
      clearTimeout(existingStaleTimeout);
      staleConnectionTimeouts.current.delete(systemId);
    }

    // If already connected or connecting to this system, just return
    if (currentSystemId === systemId && connectionRef.current?.state === 'Connected') {
      console.debug(`[SignalR] Already connected to system ${systemId}`);
      return;
    }

    // Prevent race condition: if we're already in the process of connecting to this system, skip
    if (pendingConnectionSystemId === systemId) {
      console.debug(`[SignalR] Connection to system ${systemId} already in progress`);
      return;
    }

    // Mark that we're connecting to this system
    pendingConnectionSystemId = systemId;

    // Leave old system group before connecting to new one
    if (currentSystemId && currentSystemId !== systemId) {
      const connectionId = connectionRef.current?.connectionId;
      if (connectionId) {
        await leaveSystemGroup(currentSystemId, connectionId);
      }
      cleanupConnection();
    }

    setCurrentSystemId(systemId);

    // Build the connection URL - serverless SignalR connects to base URL
    const connectionUrl = new URL(hubUrl);
    connectionUrl.searchParams.append('userId', account.username || account.localAccountId);

    const newConnection = new HubConnectionBuilder()
      .withUrl(connectionUrl.toString(), {
        accessTokenFactory: async () => {
          const currentAccount = getActiveAccount();
          if (!currentAccount) {
            throw new Error('No active account found');
          }

          const tokenResponse = await msalInstance!.acquireTokenSilent({
            scopes: protectedResources.fleetAPI.scopes,
            account: currentAccount
          });
          return tokenResponse.accessToken;
        }
      })
      .withAutomaticReconnect(RECONNECT_DELAYS)
      .configureLogging(LogLevel.Information)
      .build();

    connectionRef.current = newConnection;

    newConnection.onclose(err => {
      console.log('[SignalR] Connection closed');
      setIsConnected(false);
      setError(err ? new Error(`Connection closed: ${err.message}`) : new Error("Connection closed"));
    });

    newConnection.onreconnecting(err => {
      console.log('[SignalR] Reconnecting...', err?.message);
      setIsConnected(false);
      setError(new Error(`Reconnecting: ${err?.message || 'Unknown error'}`));
    });

    newConnection.onreconnected(() => {
      console.log('[SignalR] Reconnected successfully');
      setError(null);
      setIsConnected(true);
      setHasPersistedError(false); // Immediately clear persisted error flag
    });

    let retryCount = 0;
    while (retryCount < MAX_CONNECTION_RETRIES) {
      try {
        await newConnection.start();
        console.log(`[SignalR] Connected to system ${systemId} successfully`);
        pendingConnectionSystemId = null; // Clear pending flag on success

        // Join the system group using connection-based membership
        const connectionId = newConnection.connectionId;
        if (connectionId) {
          await joinSystemGroup(systemId, connectionId);
        }

        setIsConnected(true);
        setError(null);
        setHasPersistedError(false); // Clear persisted error flag on successful connection
        return;
      } catch (err) {
        retryCount++;
        console.error(`[SignalR] Connection attempt ${retryCount} failed:`, err);

        if (retryCount >= MAX_CONNECTION_RETRIES) {
          pendingConnectionSystemId = null; // Clear pending flag on failure
          connectionRef.current = null;
          setIsConnected(false);
          setError(err instanceof Error ? err : new Error('Connection failed after retries'));
          throw err;
        }
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_BASE * retryCount));
      }
    }
  }, [hubUrl, msalInstance, getActiveAccount, cleanupConnection, currentSystemId, leaveSystemGroup, joinSystemGroup]);

  const scheduleDisconnectFromSystem = useCallback((systemId: string) => {
    // Don't schedule disconnect if not connected to this system
    if (currentSystemId !== systemId) {
      return;
    }

    // Check ref (synchronous, always current) - don't disconnect during active refresh
    if (activeRefreshesRef.current.has(systemId)) {
      console.log(`[SignalR] Not disconnecting from system ${systemId} - active refresh in progress`);
      return;
    }

    // Schedule disconnect after 30 seconds (enough time to navigate between pages)
    const timeout = setTimeout(async () => {
      // Re-check active refresh when timer fires (not just when scheduling)
      if (activeRefreshesRef.current.has(systemId)) {
        console.log(`[SignalR] Cancelled scheduled disconnect from system ${systemId} - active refresh still in progress`);
        systemConnectionTimeouts.current.delete(systemId);
        return;
      }

      console.log(`[SignalR] Scheduled disconnect from system ${systemId}`);

      // Leave the group before disconnecting
      const connectionId = connectionRef.current?.connectionId;
      if (connectionId) {
        await leaveSystemGroup(systemId, connectionId);
      }

      cleanupConnection();
      setIsConnected(false);
      setCurrentSystemId(null);
      setError(null);
      systemConnectionTimeouts.current.delete(systemId);
    }, 30000);  // 30 second grace period

    systemConnectionTimeouts.current.set(systemId, timeout);
    console.log(`[SignalR] Scheduled disconnect from system ${systemId} in 30 seconds`);
  }, [currentSystemId, cleanupConnection, leaveSystemGroup]);

  const disconnect = useCallback(async () => {
    console.log('[SignalR] Disconnecting immediately...');

    // Clear all pending timeouts
    systemConnectionTimeouts.current.forEach(timeout => clearTimeout(timeout));
    systemConnectionTimeouts.current.clear();

    cleanupConnection();
    setIsConnected(false);
    setCurrentSystemId(null);
    setError(null);
  }, [cleanupConnection]);

  const isRefreshActive = useCallback((systemId: string) => {
    return activeRefreshesRef.current.has(systemId);
  }, []);

  // Artifact event handlers - using proper TanStack Query patterns
  useEffect(() => {
    if (!isConnected || !connectionRef.current) return;

    console.debug('[SignalR] Registering event handlers...');

    const connection = connectionRef.current;

    const handleArtifactStatusUpdate = (notification: ArtifactStatusNotification) => {
      console.debug('[SignalR] Artifact status update received:', notification);

      const artifactId = notification.artifactId;
      const systemId = notification.systemId;
      const state: SystemArtifactState = notification.state;
      const message = notification.message;

      console.debug(`[SignalR] Processing artifact update: artifactId=${artifactId}, state=${state}`);

      // Show user notification
      showArtifactNotification(artifactId, state, systemId, message);

      // Invalidate relevant queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: ['systems', 'detail', systemId, 'system-artifacts'],
        exact: false
      });

      console.debug(`[SignalR] Artifact update processed: artifactId=${artifactId}, state=${state}`);
    };

    // Register unified event handler for all artifact status changes
    connection.on('ArtifactStatusUpdate', handleArtifactStatusUpdate);
    connection.on('SystemDataRefreshUpdate', handleRefreshUpdate);

    console.debug('[SignalR] Event handlers registered');

    return () => {
      console.debug('[SignalR] Unregistering event handlers...');

      connection.off('ArtifactStatusUpdate', handleArtifactStatusUpdate);
      connection.off('SystemDataRefreshUpdate', handleRefreshUpdate);
    };
  }, [isConnected, queryClient, showArtifactNotification, handleRefreshUpdate]);

  // No automatic connection - only connect when system is requested

  // Track current system ID in a ref for cleanup
  const currentSystemIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentSystemIdRef.current = currentSystemId;
  }, [currentSystemId]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Leave current group on unmount
      const systemIdToLeave = currentSystemIdRef.current;
      const connectionIdToLeave = connectionRef.current?.connectionId;
      if (systemIdToLeave && connectionIdToLeave) {
        leaveSystemGroup(systemIdToLeave, connectionIdToLeave).catch(err =>
          console.error('Failed to leave group on unmount:', err)
        );
      }

      cleanupConnection();
      // Clear all active refresh timeouts
      activeRefreshTimeouts.current.forEach(timeout => clearTimeout(timeout));
      activeRefreshTimeouts.current.clear();
    };
  }, [cleanupConnection, leaveSystemGroup]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupConnection();
      // Clear all active refresh timeouts
      activeRefreshTimeouts.current.forEach(timeout => clearTimeout(timeout));
      activeRefreshTimeouts.current.clear();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cleanupConnection]);

  return (
    <SignalRContext.Provider value={{
      connection: connectionRef.current,
      isConnected,
      error,
      hasPersistedError,
      connectToSystem,
      scheduleDisconnectFromSystem,
      disconnect,
      currentSystemId,
      registerActiveRefresh,
      unregisterActiveRefresh,
      isRefreshActive,
      leaveSystemGroup
    }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context: SignalRContextType = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
};